/* ==========================================================================
   Support hours — the live "Open until 5pm" line.  #176

   Reads the JSON that support-hours-data.liquid renders, works out where we
   are in the week, and writes a sentence into every [data-support-hours-line]
   on the page.

   ──────────────────────────────────────────────────────────────────────────
   IT RUNS ON AUSTRALIAN EASTERN TIME, NOT THE VISITOR'S CLOCK.

   This is the whole correctness question and it is easy to get wrong. A
   customer in Perth at 2pm is looking at a Sydney team at 4pm; a customer in
   London is looking at one that is asleep. new Date().getHours() returns the
   visitor's hour, so a line built on it would tell a Perth customer we shut
   two hours from now when we shut in one minute.

   Intl.DateTimeFormat with an explicit timeZone is the fix, and it handles
   daylight saving on its own — which matters, because AEST and AEDT differ by
   an hour and the changeover is exactly when a hand-rolled offset breaks.

   Sydney and Melbourne share a timezone, so one zone covers both branches.
   The day that stops being true — a Perth or Brisbane warehouse — this needs
   a zone per location, and support_hours has no location field by design.
   ──────────────────────────────────────────────────────────────────────────

   WHAT IT WRITES

     Open, more than an hour left      "Open until 5pm"
     Open, closing within the hour     "Closing at 5pm"
     Closed, opens later today         "Opens at 8am"
     Closed, opens tomorrow            "Opens 8am tomorrow"
     Closed, opens further out         "Opens Monday, 8am"
     A note on today's record          the note, verbatim
     No data at all                    nothing — the markup's own text stays

   That last one is deliberate. A missing record leaves the static line the
   theme already rendered, so the failure mode is "slightly out of date"
   rather than "blank" or "confidently wrong".
   ========================================================================== */

(function () {
  'use strict';

  var ZONE = 'Australia/Sydney';
  var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  /* ---- reading the data -------------------------------------------------- */

  function readData() {
    var tag = document.querySelector('[data-support-hours]');
    if (!tag) return null;
    try {
      var parsed = JSON.parse(tag.textContent);
      return (parsed && parsed.days && parsed.days.length) ? parsed.days : null;
    } catch (err) {
      console.error('[support-hours] The hours JSON did not parse:', err);
      return null;
    }
  }

  /* ---- the clock --------------------------------------------------------- */

  /* Returns { weekday, minutes, ymd } in the store's timezone.

     formatToParts rather than string parsing: a formatted date string differs
     by browser and by locale, and picking it apart with a regex is how this
     sort of code acquires a bug that only appears on someone else's phone. */
  function storeNow() {
    var parts;
    try {
      parts = new Intl.DateTimeFormat('en-AU', {
        timeZone: ZONE,
        weekday: 'long',
        hour: '2-digit',
        minute: '2-digit',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour12: false
      }).formatToParts(new Date());
    } catch (err) {
      /* An engine without the timezone database. Rare, but the honest response
         is to render nothing rather than fall back to the visitor's clock and
         state a time that is wrong for most of the country. */
      console.error('[support-hours] This browser cannot resolve ' + ZONE + '. Leaving the static text.', err);
      return null;
    }

    var out = {};
    parts.forEach(function (p) { out[p.type] = p.value; });

    /* "24" appears at midnight in some implementations of hour12:false. */
    var hour = parseInt(out.hour, 10) % 24;

    return {
      weekday: out.weekday,
      minutes: hour * 60 + parseInt(out.minute, 10),
      ymd: out.year + '-' + out.month + '-' + out.day,
      mmdd: out.month + '-' + out.day
    };
  }

  function toMinutes(hhmm) {
    if (!hhmm) return null;
    var bits = String(hhmm).split(':');
    if (bits.length < 2) return null;
    var h = parseInt(bits[0], 10);
    var m = parseInt(bits[1], 10);
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
  }

  /* "17:00" -> "5pm", "16:30" -> "4.30pm".
     A full stop rather than a colon: "4.30pm" is how the rest of the site
     writes it, and the mega menu's own hours line already does. */
  function label(hhmm) {
    var total = toMinutes(hhmm);
    if (total === null) return '';
    var h = Math.floor(total / 60);
    var m = total % 60;
    var suffix = h < 12 ? 'am' : 'pm';
    var display = h % 12;
    if (display === 0) display = 12;
    return display + (m ? '.' + String(m).padStart(2, '0') : '') + suffix;
  }

  /* ---- choosing the day -------------------------------------------------- */

  /* A record carrying a `date` overrides the weekday one for that calendar
     day — Christmas Eve closing early. Checked first for that reason. */
  function dayFor(days, weekday, ymd, mmdd) {
    var dated = null;
    var weekly = null;

    days.forEach(function (d) {
      if (d.date) {
        /* An annual record matches month-and-day and ignores the year, so
           Christmas works every year rather than once. Same rule and the same
           field name as the holiday record on a location, so anyone who has
           read charged-nights.liquid already knows this branch.

           A one-off still matches the full date. Getting this backwards means
           a single closure repeating forever, which is the quieter of the two
           failures and therefore the worse one. */
        var stored = String(d.date).slice(0, 10);
        var hit = d.repeats
          ? (mmdd && stored.slice(5) === mmdd)
          : (ymd && stored === ymd);
        if (hit) dated = d;
      } else if (d.weekday === weekday) {
        weekly = d;
      }
    });

    return dated || weekly;
  }

  /* Add whole days to a YYYY-MM-DD and return the new date plus its weekday,
     both in the store's timezone.

     Anchored at MIDDAY UTC rather than midnight. Stepping 86400000ms from
     midnight lands at 23:00 of the previous day across a daylight-saving
     change, so the weekday comes out wrong twice a year — the identical trap
     charged-nights.liquid documents for its own night walk, and the reason it
     anchors at midday too. */
  function addDays(ymd, n) {
    var bits = ymd.split('-');
    var anchor = Date.UTC(+bits[0], +bits[1] - 1, +bits[2], 12, 0, 0);
    var moved = new Date(anchor + n * 86400000);

    var parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: ZONE, weekday: 'long',
      year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(moved);

    var out = {};
    parts.forEach(function (p) { out[p.type] = p.value; });
    return {
      ymd: out.year + '-' + out.month + '-' + out.day,
      mmdd: out.month + '-' + out.day,
      weekday: out.weekday
    };
  }

  /* The next day we are open, searching forward up to a week.

     ── THIS USED TO IGNORE DATED RECORDS AND WAS WRONG ───────────────────
     The first version matched on weekday alone and skipped anything carrying
     a date. So with Christmas Day closed as a dated record, Christmas Eve
     still read "Opens 8am tomorrow" — the one day of the year the sentence
     had to be right. Caught by Ben asking what happens with holiday hours,
     not by me testing it.

     It now resolves each forward day exactly the way TODAY is resolved: a
     dated record for that calendar date beats the weekday one. dayFor already
     does that, so the fix is to CALL it rather than re-implement half of it —
     which is how the two drifted apart to begin with.

     Returns { day, offset, weekday } or null. Null means all seven forward
     days are closed, which is a data problem rather than a state to render. */
  function nextOpen(days, fromYmd) {
    for (var step = 1; step <= 7; step++) {
      var at = addDays(fromYmd, step);
      var candidate = dayFor(days, at.weekday, at.ymd, at.mmdd);
      if (candidate && candidate.open && toMinutes(candidate.from) !== null) {
        return { day: candidate, offset: step, weekday: at.weekday };
      }
    }
    return null;
  }

  /* ---- the sentence ------------------------------------------------------ */

  function sentence(days, now) {
    var today = dayFor(days, now.weekday, now.ymd, now.mmdd);

    /* A note is an override with a reason — "Closed for the public holiday"
       tells a customer more than any computed line, so it wins outright. */
    if (today && today.note) return today.note;

    var index = DAYS.indexOf(now.weekday);
    if (index === -1) return '';

    if (today && today.open) {
      var from = toMinutes(today.from);
      var to = toMinutes(today.to);

      if (from !== null && to !== null) {
        if (now.minutes < from) return 'Opens at ' + label(today.from);
        if (now.minutes < to) {
          var left = to - now.minutes;
          return left <= 60
            ? 'Closing at ' + label(today.to)
            : 'Open until ' + label(today.to) + ' today';
        }
        /* Past closing — fall through to the next open day. */
      }
    }

    var next = nextOpen(days, now.ymd);
    if (!next) return '';

    if (next.offset === 1) return 'Opens ' + label(next.day.from) + ' tomorrow';

    /* next.weekday, not next.day.weekday — a dated record's weekday field is
       usually blank, because the date is what identifies it. */
    return 'Opens ' + next.weekday + ', ' + label(next.day.from);
  }

  /* ---- the week, for the Support hours block ----------------------------- */

  /* The listed hours under the contact rows were static theme text while the
     line above them was live — so the two could disagree on the same screen,
     which is worse than both being stale. This renders the list from the same
     records.

     GROUPED, not seven lines. "Mon-Fri: 8am-5pm" is what a person writes, and
     what the block said before this. Consecutive days sharing the same hours
     collapse into a range; a day that differs stands alone.

     DATED RECORDS ARE DELIBERATELY EXCLUDED. This is the normal week, and a
     one-off holiday belongs in the live line above, where it is relevant on
     the day rather than misleading for a fortnight either side. */

  var SHORT = { Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu',
                Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun' };
  var ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  function weekLines(days) {
    var rows = ORDER.map(function (name) {
      var found = null;
      days.forEach(function (d) { if (!d.date && d.weekday === name) found = d; });
      if (!found) return null;
      var text = found.open && toMinutes(found.from) !== null && toMinutes(found.to) !== null
        ? label(found.from) + '-' + label(found.to)
        : 'closed';
      return { name: name, text: text };
    });

    if (rows.indexOf(null) !== -1) return null;   // an incomplete week is not ours to guess at

    var out = [];
    var i = 0;
    while (i < rows.length) {
      var j = i;
      while (j + 1 < rows.length && rows[j + 1].text === rows[i].text) j++;
      var head = SHORT[rows[i].name];
      var tail = SHORT[rows[j].name];
      out.push((i === j ? head : head + '-' + tail) + ': ' + rows[i].text);
      i = j + 1;
    }
    return out;
  }

  function paintWeek(days) {
    var targets = document.querySelectorAll('[data-support-hours-week]');
    if (!targets.length) return;

    var lines = weekLines(days);
    if (!lines) return;           // leave the static text

    targets.forEach(function (el) {
      el.textContent = '';
      lines.forEach(function (line, n) {
        if (n) el.appendChild(document.createElement('br'));
        el.appendChild(document.createTextNode(line));
      });
    });
  }

  /* ---- writing it out ---------------------------------------------------- */

  function paint() {
    var days = readData();
    if (!days) return;            // leave the static text

    paintWeek(days);

    var targets = document.querySelectorAll('[data-support-hours-line]');
    if (!targets.length) return;

    var now = storeNow();
    if (!now) return;             // leave the static text

    var text = sentence(days, now);
    if (!text) return;            // leave the static text

    var open = (function () {
      var t = dayFor(days, now.weekday, now.ymd, now.mmdd);
      if (!t || !t.open) return false;
      var from = toMinutes(t.from), to = toMinutes(t.to);
      return from !== null && to !== null && now.minutes >= from && now.minutes < to;
    })();

    targets.forEach(function (el) {
      el.textContent = text;
      el.dataset.supportOpen = String(open);
    });
  }

  /* ========================================================================
     PER-LOCATION TRADING HOURS — 16a
     ========================================================================

     WHY THIS LIVES IN THE SUPPORT HOURS FILE, which is not what its name
     suggests. Everything above — the Australia/Sydney clock, the 12-hour
     parser, dayFor, addDays, nextOpen, the grouping — is needed identically
     here. A second file would mean a second copy of the timezone handling and
     a second place for the AEST/AEDT changeover to be got wrong. Two sources
     for one fact is what this project keeps removing, so the file is misnamed
     rather than duplicated. Its job is hours from metaobjects.

     WHAT IS DIFFERENT FROM SUPPORT HOURS, and it is only two things:

       1. A HOLIDAY NAMES ITSELF. Support hours use a per-record note. A
          location has a separate holidays list, so a closed day can say
          "Closed for Christmas Day, open again Mon, 8am" — which answers the
          question a bare "Closed" leaves open: shut on purpose, or broken.

       2. THE WEEK CARRIES TWO WINDOWS, not one. Pickup and return are what a
          customer actually needs and they are narrower than the warehouse's
          own hours, so the rows are structured rather than strings.
     ======================================================================== */

  function readLocations() {
    var tag = document.querySelector('script[type="application/json"][data-location-hours]');
    if (!tag) return null;
    try {
      var parsed = JSON.parse(tag.textContent);
      return (parsed && parsed.locations && parsed.locations.length) ? parsed.locations : null;
    } catch (err) {
      /* Malformed JSON leaves the static text rather than throwing. A line
         that is slightly stale beats a panel that is blank. */
      return null;
    }
  }

  /* A holiday matches on the exact date, or on month-and-day when it repeats
     annually. The same test charged-nights.liquid uses — and the reason its
     Liquid version insists on .value: without it every holiday reads as
     repeating and matches a year early. */
  function holidayFor(holidays, ymd, mmdd) {
    if (!holidays) return null;
    for (var i = 0; i < holidays.length; i++) {
      var h = holidays[i];
      if (!h.date) continue;
      var hy = String(h.date).slice(0, 10);
      if (h.repeats ? hy.slice(5) === mmdd : hy === ymd) return h;
    }
    return null;
  }

  /* Returns { text, state }. State drives the colour: open, soon, shut.
     Three states, not four — a holiday is shut with a better sentence. */
  function locationSentence(loc, now) {
    var days = loc.days || [];
    var today = dayFor(days, now.weekday, now.ymd, now.mmdd);
    var holiday = holidayFor(loc.holidays, now.ymd, now.mmdd);

    function opensAgain() {
      var next = nextOpen(days, now.ymd);
      if (!next) return '';
      return next.offset === 1
        ? 'tomorrow, ' + label(next.day.from)
        : next.weekday + ', ' + label(next.day.from);
    }

    if (holiday && holiday.name) {
      var again = opensAgain();
      return {
        text: 'Closed for ' + holiday.name + (again ? ', open again ' + again : ''),
        state: 'shut'
      };
    }

    /* A record's own note beats anything computed — it is a human saying
       something a clock cannot work out. */
    if (today && today.note) return { text: today.note, state: today.open ? 'open' : 'shut' };

    if (today && today.open) {
      var from = toMinutes(today.from);
      var to = toMinutes(today.to);
      if (from !== null && to !== null) {
        if (now.minutes < from) return { text: 'Opens at ' + label(today.from), state: 'shut' };
        if (now.minutes < to) {
          /* "Open until 3pm" at 2.20pm is true and useless — someone reads it
             and plans a trip they will not make. Inside the last hour the
             line says so instead. */
          return now.minutes >= to - 60
            ? { text: 'Closes soon \u00b7 ' + label(today.to), state: 'soon' }
            : { text: 'Open until ' + label(today.to), state: 'open' };
        }
      }
    }

    var back = opensAgain();
    return { text: back ? 'Closed, open again ' + back : 'Closed', state: 'shut' };
  }

  /* The grouped week as structured rows rather than strings, because each
     open day carries two windows.

     GROUPED because "Mon - Sat" is what a person writes, and because it stays
     short while the two warehouses agree and grows only when they drift —
     which is the entire argument for per-location hours.

     DATED RECORDS EXCLUDED: this is the normal week. A one-off belongs in the
     live line above, where it is true on the day rather than misleading for a
     fortnight either side. */
  function locationWeek(days) {
    var rows = ORDER.map(function (name) {
      var found = null;
      days.forEach(function (d) { if (!d.date && d.weekday === name) found = d; });
      if (!found) return null;

      var pickup = (toMinutes(found.pickupFrom) !== null && toMinutes(found.pickupTo) !== null)
        ? label(found.pickupFrom) + ' - ' + label(found.pickupTo) : '';
      var ret = (toMinutes(found.returnFrom) !== null && toMinutes(found.returnTo) !== null)
        ? label(found.returnFrom) + ' - ' + label(found.returnTo) : '';

      /* If the windows are empty, fall back to the warehouse's own hours AND
         say so. Labelling open hours as a pickup window would send someone at
         a time we do not hand gear over. */
      var fellBack = false;
      if (!pickup && !ret && found.open &&
          toMinutes(found.from) !== null && toMinutes(found.to) !== null) {
        pickup = label(found.from) + ' - ' + label(found.to);
        fellBack = true;
      }

      return {
        name: name,
        open: !!found.open && !!(pickup || ret),
        pickup: pickup,
        ret: ret,
        fellBack: fellBack,
        key: found.open ? (pickup + '|' + ret) : 'closed'
      };
    });

    if (rows.indexOf(null) !== -1) return null;   // an incomplete week is not ours to guess at

    var out = [];
    var i = 0;
    while (i < rows.length) {
      var j = i;
      while (j + 1 < rows.length && rows[j + 1].key === rows[i].key) j++;
      out.push({
        days: i === j ? SHORT[rows[i].name] : SHORT[rows[i].name] + ' - ' + SHORT[rows[j].name],
        first: rows[i].name,
        last: rows[j].name,
        open: rows[i].open,
        pickup: rows[i].pickup,
        ret: rows[i].ret,
        fellBack: rows[i].fellBack
      });
      i = j + 1;
    }
    return out;
  }

  function cell(cls, text) {
    var el = document.createElement('span');
    el.className = cls;
    el.textContent = text;
    return el;
  }

  function paintLocations() {
    /* window.__djwHours says how far this got. Set BEFORE the guards, so a
       failing case reports itself rather than leaving "__djwHours is not
       defined" -- which is exactly how the first chat diagnostic was written
       and exactly why it was useless. */
    var report = window.__djwHours = window.__djwHours || {};

    var locations = readLocations();
    report.dataBlockFound = !!document.querySelector('script[data-location-hours]');
    report.locationsParsed = locations ? locations.length : 0;
    report.handles = locations ? locations.map(function (l) { return l.handle; }) : [];
    report.rowsInDom = document.querySelectorAll('[data-location-hours-line]').length;
    report.rowScopes = [].map.call(
      document.querySelectorAll('[data-location]'),
      function (el) { return el.getAttribute('data-location'); }
    );
    if (!locations) { report.stopped = 'no data block, or it parsed empty'; return; }

    var now = storeNow();
    if (!now) { report.stopped = 'the clock returned nothing'; return; }
    report.now = now.weekday + ' ' + now.ymd + ' ' + now.minutes + 'min';
    report.matched = 0;

    locations.forEach(function (loc) {
      var scope = '[data-location="' + loc.handle + '"]';

      var lines = document.querySelectorAll(scope + ' [data-location-hours-line]');
      if (lines.length) {
        report.matched++;
        var said = locationSentence(loc, now);
        if (said.text) {
          lines.forEach(function (el) {
            el.textContent = said.text;
            el.dataset.state = said.state;
          });
        }
      }

      var weeks = document.querySelectorAll(scope + ' [data-location-hours-week]');
      if (!weeks.length) return;

      var grouped = locationWeek(loc.days || []);
      if (!grouped) return;       // leave whatever the theme rendered

      weeks.forEach(function (el) {
        el.textContent = '';

        /* Headers once, not per row. They only earn their place if at least
           one row actually has windows — an all-closed week with "Pickup" and
           "Return" standing over nothing reads as an error. */
        var anyOpen = grouped.some(function (r) { return r.open; });
        if (anyOpen) {
          el.appendChild(cell('mm-hours__h', ''));
          el.appendChild(cell('mm-hours__h', grouped[0].fellBack ? 'Open' : 'Pickup'));
          el.appendChild(cell('mm-hours__h', grouped[0].fellBack ? '' : 'Return'));
        }

        grouped.forEach(function (row) {
          var span = ORDER.slice(ORDER.indexOf(row.first), ORDER.indexOf(row.last) + 1);
          /* Today is marked so the panel answers "which day is this?" without
             the customer counting rows. It only matters when the panel is
             opened while closed, at which point a window with no day attached
             says nothing at all. */
          var isToday = span.indexOf(now.weekday) !== -1;

          var day = cell('mm-hours__days', row.days);
          if (isToday) day.dataset.today = 'true';
          el.appendChild(day);

          if (!row.open) {
            el.appendChild(cell('mm-hours__shut', 'Closed'));
          } else if (row.fellBack) {
            el.appendChild(cell('mm-hours__v', row.pickup));
            el.appendChild(cell('mm-hours__v', ''));
          } else {
            el.appendChild(cell('mm-hours__v', row.pickup || '\u2014'));
            el.appendChild(cell('mm-hours__v', row.ret || '\u2014'));
          }
        });
      });
    });
  }

  /* One entry point, so the two halves cannot fall out of step on the minute
     tick or in the theme editor. */
  function paintAll() {
    paint();
    paintLocations();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', paintAll);
  } else {
    paintAll();
  }

  /* Repaint on the minute rather than on a timer of our own choosing: someone
     with the menu open at 4.59pm should see the line change. Aligned to the
     minute boundary so it never sits a half-minute behind. */
  setTimeout(function tick() {
    paintAll();
    setTimeout(tick, 60000);
  }, (60 - new Date().getSeconds()) * 1000);

  /* The theme editor re-renders sections without reloading the page. */
  document.addEventListener('shopify:section:load', paintAll);
})();
